import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, PlusCircle, Lightbulb } from "lucide-react";
import { motion } from "framer-motion";

interface EmptyTopicsProps {
  onGenerate: () => void;
  isGenerating: boolean;
}

export const EmptyTopics = ({ onGenerate, isGenerating }: EmptyTopicsProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 shadow-sm overflow-hidden"
    >
      <div className="p-8 flex flex-col items-center justify-center text-center">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-full mb-4">
          <Sparkles className="h-6 w-6 text-blue-500 dark:text-blue-400" />
        </div>
        
        <h3 className="text-xl font-semibold text-zinc-900 dark:text-white mb-2">No Topics Yet</h3>
        
        <p className="text-zinc-500 dark:text-zinc-400 max-w-md mb-6">
          Generate content topics based on your website analysis or create custom topics to start producing engaging content.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={onGenerate}
            disabled={isGenerating}
            className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm dark:shadow-blue-900/20"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Topics
              </>
            )}
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            className="border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-white transition-all duration-200"
          >
            <PlusCircle className="mr-2 h-5 w-5" />
            Create Manually
          </Button>
        </div>
      </div>
      
      <div className="mt-12 w-full max-w-3xl mx-auto pb-12">
        <h4 className="text-center text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-6">How it works</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <div className="bg-zinc-50 dark:bg-zinc-800 p-4 rounded-lg border border-zinc-200 dark:border-zinc-700 h-full flex flex-col">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mb-3">
              1
            </div>
            <h4 className="font-medium text-zinc-800 dark:text-white mb-1">Generate Topics</h4>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 flex-1">
              Our AI creates relevant topics based on your industry and audience
            </p>
          </div>
          
          <div className="bg-zinc-50 dark:bg-zinc-800 p-4 rounded-lg border border-zinc-200 dark:border-zinc-700 h-full flex flex-col">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mb-3">
              2
            </div>
            <h4 className="font-medium text-zinc-800 dark:text-white mb-1">Customize</h4>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 flex-1">
              Edit and refine topics to match your specific content needs
            </p>
          </div>
          
          <div className="bg-zinc-50 dark:bg-zinc-800 p-4 rounded-lg border border-zinc-200 dark:border-zinc-700 h-full flex flex-col">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mb-3">
              3
            </div>
            <h4 className="font-medium text-zinc-800 dark:text-white mb-1">Create Content</h4>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 flex-1">
              Generate engaging content across multiple platforms
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};